import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Wallet, Users, HandCoins, PiggyBank, Snowflake, Plus, ArrowUpFromLine, ArrowDownToLine, Banknote, Search, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

import ReceiveMoneyDialog from '../components/ReceiveMoneyDialog';

const fmtINR = (n) => `₹${(Math.round(Number(n || 0) * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const LOAN_STATUS_TONE = {
  active: 'bg-blue-500/15 text-blue-700',
  overdue: 'bg-rose-500/15 text-rose-700',
  repaid: 'bg-emerald-500/15 text-emerald-700',
  written_off: 'bg-neutral-500/15 text-neutral-700',
};

export default function AdminWalletPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loans, setLoans] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state
  const [walletActionOpen, setWalletActionOpen] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [actionKind, setActionKind] = useState('credit'); // credit | debit | freeze | adjust | loan | deposit_in | deposit_out
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [interestRate, setInterestRate] = useState('0');
  const [termDays, setTermDays] = useState('30');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [repayOpen, setRepayOpen] = useState(false);
  const [repayLoan, setRepayLoan] = useState(null);
  const [repayAmount, setRepayAmount] = useState('');

  // iter231 — Receive Money wizard + contribution requests queue
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [confirmingReqId, setConfirmingReqId] = useState(null);

  // iter232 — Pending Cash Out requests queue (mirror of contribution requests)
  const [pendingCashOuts, setPendingCashOuts] = useState([]);
  const [confirmingCashOutId, setConfirmingCashOutId] = useState(null);

  const fetchPendingRequests = async () => {
    try {
      const r = await api.get('/contribution-requests?status=pending');
      setPendingRequests(r.data?.requests || []);
    } catch (e) { /* silent */ }
  };
  const fetchPendingCashOuts = async () => {
    try {
      const r = await api.get('/cash-out-requests?status=pending');
      setPendingCashOuts(r.data?.requests || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (user?.is_super_admin) {
      fetchPendingRequests();
      fetchPendingCashOuts();
    }
  }, [user?.is_super_admin]);

  const confirmRequest = async (req) => {
    if (!window.confirm(`Confirm receipt of ₹${Number(req.amount).toLocaleString('en-IN')} from ${req.payer_name} for ${req.group_name}?`)) return;
    setConfirmingReqId(req.id);
    try {
      await api.post(`/contribution-requests/${req.id}/confirm`);
      toast.success('Contribution confirmed');
      fetchPendingRequests();
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    } finally {
      setConfirmingReqId(null);
    }
  };
  const cancelRequest = async (req) => {
    if (!window.confirm('Cancel this pending request?')) return;
    try {
      await api.post(`/contribution-requests/${req.id}/cancel`);
      fetchPendingRequests();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  // iter232 — Confirm/cancel cash-out handlers
  const confirmCashOut = async (req) => {
    if (!window.confirm(`Hand ₹${Number(req.amount).toLocaleString('en-IN')} (${req.method}) to ${req.payee_name} out of ${req.group_name}?\nThis will debit their credit and the group fund.`)) return;
    setConfirmingCashOutId(req.id);
    try {
      await api.post(`/cash-out-requests/${req.id}/confirm`);
      toast.success('Cash-out confirmed');
      fetchPendingCashOuts();
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    } finally {
      setConfirmingCashOutId(null);
    }
  };
  const cancelCashOut = async (req) => {
    if (!window.confirm('Cancel this pending cash-out request?')) return;
    try {
      await api.post(`/cash-out-requests/${req.id}/cancel`);
      fetchPendingCashOuts();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, w, l, d] = await Promise.all([
        api.get('/admin/wallets-analytics/summary'),
        api.get('/admin/wallets'),
        api.get('/admin/loans'),
        api.get('/admin/deposits'),
      ]);
      setSummary(s.data);
      setWallets(w.data.rows || []);
      setLoans(l.data.rows || []);
      setDeposits(d.data.rows || []);
    } catch (e) {
      toast.error('Failed to load wallet admin data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  if (!user?.is_super_admin && !user?.is_admin) {
    return (
      <Card className="p-8 text-center">
        <div className="text-lg font-semibold">Admin access required</div>
        <p className="text-sm text-muted-foreground mt-2">Only admins can manage the internal wallet.</p>
      </Card>
    );
  }

  const openAction = (kind, uid, name) => {
    setActionKind(kind);
    setSelectedUid(uid);
    setSelectedName(name || '');
    setAmount('');
    setNote('');
    setInterestRate('0');
    setTermDays('30');
    setMethod('cash');
    setReference('');
    setWalletActionOpen(true);
  };

  const submitAction = async () => {
    if (!selectedUid) return;
    setSubmitting(true);
    try {
      if (actionKind === 'credit') {
        await api.post(`/admin/wallets/${selectedUid}/credit`, { amount: parseFloat(amount), note });
        toast.success(`Credited ${fmtINR(amount)} to ${selectedName}`);
      } else if (actionKind === 'debit') {
        await api.post(`/admin/wallets/${selectedUid}/debit`, { amount: parseFloat(amount), note });
        toast.success(`Debited ${fmtINR(amount)} from ${selectedName}`);
      } else if (actionKind === 'adjust') {
        await api.post(`/admin/wallets/${selectedUid}/adjust`, { delta: parseFloat(amount), note });
        toast.success(`Adjustment applied`);
      } else if (actionKind === 'freeze') {
        const target = wallets.find(w => w.user_id === selectedUid);
        await api.post(`/admin/wallets/${selectedUid}/freeze`, { is_frozen: !target?.is_frozen });
        toast.success(`${target?.is_frozen ? 'Unfrozen' : 'Frozen'} ${selectedName}'s wallet`);
      } else if (actionKind === 'loan') {
        await api.post(`/admin/loans`, {
          user_id: selectedUid,
          principal: parseFloat(amount),
          interest_rate: parseFloat(interestRate) || 0,
          term_days: parseInt(termDays, 10),
          note,
        });
        toast.success(`Loan of ${fmtINR(amount)} issued to ${selectedName}`);
      } else if (actionKind === 'deposit_in' || actionKind === 'deposit_out') {
        await api.post(`/admin/deposits`, {
          user_id: selectedUid,
          amount: parseFloat(amount),
          direction: actionKind === 'deposit_in' ? 'in' : 'out',
          method, reference, note,
        });
        toast.success(`Deposit recorded`);
      }
      setWalletActionOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRepay = async () => {
    if (!repayLoan) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/loans/${repayLoan.id}/repay`, { amount: parseFloat(repayAmount) });
      toast.success('Repayment recorded');
      setRepayOpen(false);
      setRepayLoan(null);
      setRepayAmount('');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repay failed');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredWallets = wallets.filter(w => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (w.name || '').toLowerCase().includes(s) || (w.phone || '').includes(s);
  });

  if (loading) return <div className="space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-96 w-full" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-wallet-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
          <Wallet className="w-7 h-7 text-primary" />
          Wallet Admin
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Track real cash handed over inside the trust network.</p>
      </div>

      {/* iter231 — Receive Money CTA + pending contribution request queue */}
      {user?.is_super_admin && (
        <>
          <div className="flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border-2 border-emerald-500/20 rounded-2xl p-4">
            <div>
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <Banknote className="w-4 h-4 text-emerald-600" /> Received cash from someone?
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Log it here — routes correctly to wallet, group, loan repay, or P2P.
              </p>
            </div>
            <Button
              onClick={() => setReceiveOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="open-receive-money-btn"
            >
              <Banknote className="w-4 h-4 mr-1.5" /> Receive Money
            </Button>
          </div>

          {pendingRequests.length > 0 && (
            <Card className="p-3" data-testid="pending-contrib-requests-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">📩 Pending contribution requests</p>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 font-semibold">
                  {pendingRequests.length}
                </span>
              </div>
              <div className="space-y-2">
                {pendingRequests.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-2 bg-muted/30 border rounded-lg p-2.5 text-sm"
                    data-testid={`pending-req-${r.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">
                        {r.payer_name} → {r.group_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        ₹{Number(r.amount).toLocaleString('en-IN')} via {r.method}
                        {r.note ? ` · ${r.note}` : ''} · {fmtDate(r.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => confirmRequest(r)}
                        disabled={confirmingReqId === r.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`confirm-req-${r.id}`}
                      >
                        {confirmingReqId === r.id ? '…' : 'Confirm'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelRequest(r)}
                        className="text-red-500"
                        data-testid={`cancel-req-${r.id}`}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* iter232 — Pending Cash Out requests (mirror queue) */}
          {pendingCashOuts.length > 0 && (
            <Card className="p-3" data-testid="pending-cash-out-requests-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">💵 Pending cash-out requests</p>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 font-semibold">
                  {pendingCashOuts.length}
                </span>
              </div>
              <div className="space-y-2">
                {pendingCashOuts.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5 text-sm"
                    data-testid={`pending-cash-out-${r.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">
                        {r.payee_name} ← {r.group_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        ₹{Number(r.amount).toLocaleString('en-IN')} via {r.method}
                        {r.note ? ` · ${r.note}` : ''} · {fmtDate(r.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => confirmCashOut(r)}
                        disabled={confirmingCashOutId === r.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`confirm-cash-out-${r.id}`}
                      >
                        {confirmingCashOutId === r.id ? '…' : 'Paid'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelCashOut(r)}
                        className="text-red-500"
                        data-testid={`cancel-cash-out-${r.id}`}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Summary grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard icon={Users} label="Members" value={summary?.wallet_count ?? 0} testid="wallet-summary-count" />
        <SummaryCard icon={PiggyBank} label="Net balance" value={fmtINR(summary?.total_balance ?? 0)} tone="primary" testid="wallet-summary-total" />
        <SummaryCard icon={HandCoins} label="Outstanding loans" value={fmtINR(summary?.outstanding_loans ?? 0)} sub={`${summary?.open_loans ?? 0} open`} tone="amber" testid="wallet-summary-loans" />
        <SummaryCard icon={Snowflake} label="Frozen" value={summary?.frozen_wallets ?? 0} tone="rose" testid="wallet-summary-frozen" />
      </div>

      <Tabs defaultValue="wallets" className="w-full">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="wallets" data-testid="admin-tab-wallets">Wallets</TabsTrigger>
          <TabsTrigger value="loans" data-testid="admin-tab-loans">Loans</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="admin-tab-deposits">Deposits</TabsTrigger>
        </TabsList>

        {/* ---------------- Wallets tab ---------------- */}
        <TabsContent value="wallets" className="mt-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or phone"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-wallet-search"
            />
          </div>

          <Card className="divide-y divide-border">
            {filteredWallets.length === 0 && (
              <div className="p-6 text-center text-muted-foreground">No wallets match.</div>
            )}
            {filteredWallets.map((w) => (
              <div key={w.user_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-wallet-row-${w.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{w.name || 'Unknown'}</span>
                    {w.is_frozen && (
                      <span className="text-xs font-bold uppercase px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-700">Frozen</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{w.phone}</div>
                </div>
                <div className="text-right sm:text-right">
                  <div className={`font-bold tabular-nums text-lg ${w.balance > 0 ? 'text-emerald-600' : w.balance < 0 ? 'text-rose-600' : ''}`}>
                    {fmtINR(w.balance)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 justify-end">
                  <Button size="sm" variant="outline" onClick={() => openAction('credit', w.user_id, w.name)} data-testid={`credit-btn-${w.user_id}`}>
                    <ArrowUpFromLine className="w-3.5 h-3.5 mr-1" />Credit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openAction('debit', w.user_id, w.name)} data-testid={`debit-btn-${w.user_id}`}>
                    <ArrowDownToLine className="w-3.5 h-3.5 mr-1" />Debit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openAction('loan', w.user_id, w.name)} data-testid={`loan-btn-${w.user_id}`}>
                    <HandCoins className="w-3.5 h-3.5 mr-1" />Loan
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openAction('deposit_in', w.user_id, w.name)} data-testid={`cash-in-btn-${w.user_id}`}>
                    <Banknote className="w-3.5 h-3.5 mr-1" />Cash In
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openAction('freeze', w.user_id, w.name)} data-testid={`freeze-btn-${w.user_id}`}>
                    <Snowflake className="w-3.5 h-3.5" />
                  </Button>
                  {user?.is_super_admin && (
                    <Button size="sm" variant="ghost" onClick={() => openAction('adjust', w.user_id, w.name)} data-testid={`adjust-btn-${w.user_id}`}>
                      Adjust
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* ---------------- Loans tab ---------------- */}
        <TabsContent value="loans" className="mt-4 space-y-3">
          <Card className="divide-y divide-border">
            {loans.length === 0 && <div className="p-6 text-center text-muted-foreground">No loans on record.</div>}
            {loans.map((l) => (
              <div key={l.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="admin-loan-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{l.user_name || 'Unknown'}</span>
                    <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${LOAN_STATUS_TONE[l.status] || 'bg-neutral-500/15'}`}>{l.status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {fmtINR(l.principal)} + {l.interest_rate}% ({l.term_days}d) • due {fmtDate(l.due_date)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold tabular-nums">{fmtINR(l.outstanding)}</div>
                  <div className="text-xs text-muted-foreground">outstanding of {fmtINR(l.total_due)}</div>
                </div>
                {(l.status === 'active' || l.status === 'overdue') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setRepayLoan(l); setRepayAmount(String(l.outstanding)); setRepayOpen(true); }}
                    data-testid="loan-repay-btn"
                  >
                    Repay
                  </Button>
                )}
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* ---------------- Deposits tab ---------------- */}
        <TabsContent value="deposits" className="mt-4 space-y-3">
          <Card className="divide-y divide-border">
            {deposits.length === 0 && <div className="p-6 text-center text-muted-foreground">No cash deposits recorded.</div>}
            {deposits.map((d) => (
              <div key={d.id} className="p-3 flex items-center justify-between" data-testid="admin-deposit-row">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.user_name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {d.direction === 'in' ? 'Handed cash to treasurer' : 'Received cash from treasurer'} via {d.method}
                    {d.reference && ` • ref: ${d.reference}`}
                  </div>
                  <div className="text-xs text-muted-foreground">{fmtDate(d.created_at)} • by {d.actor_user_name}</div>
                </div>
                <div className={`font-bold tabular-nums ${d.direction === 'in' ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {d.direction === 'in' ? '+' : '-'}{fmtINR(d.amount)}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action modal */}
      <Dialog open={walletActionOpen} onOpenChange={setWalletActionOpen}>
        <DialogContent data-testid="wallet-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionKind === 'credit' && `Credit wallet — ${selectedName}`}
              {actionKind === 'debit' && `Debit wallet — ${selectedName}`}
              {actionKind === 'adjust' && `Adjustment — ${selectedName}`}
              {actionKind === 'freeze' && `Toggle freeze — ${selectedName}`}
              {actionKind === 'loan' && `Issue loan — ${selectedName}`}
              {actionKind === 'deposit_in' && `Record cash IN — ${selectedName}`}
              {actionKind === 'deposit_out' && `Record cash OUT — ${selectedName}`}
            </DialogTitle>
          </DialogHeader>

          {actionKind !== 'freeze' && (
            <div className="space-y-3">
              <div>
                <Label>{actionKind === 'adjust' ? 'Signed delta (positive or negative)' : 'Amount (₹)'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 500"
                  data-testid="wallet-action-amount"
                />
              </div>

              {actionKind === 'loan' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Interest rate (% p.a.)</Label>
                    <Input type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} data-testid="wallet-loan-interest" />
                    <p className="text-xs text-muted-foreground mt-1">Default 0% (no interest)</p>
                  </div>
                  <div>
                    <Label>Term</Label>
                    <Select value={termDays} onValueChange={setTermDays}>
                      <SelectTrigger data-testid="wallet-loan-term"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="180">180 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {(actionKind === 'deposit_in' || actionKind === 'deposit_out') && (
                <>
                  <div>
                    <Label>Method</Label>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger data-testid="deposit-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="bank">Bank transfer</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reference (UPI txn / cheque no. / bank ref)</Label>
                    <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" data-testid="deposit-reference" />
                  </div>
                </>
              )}

              <div>
                <Label>Note {actionKind === 'adjust' && <span className="text-rose-600">*</span>}</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why did this happen?" data-testid="wallet-action-note" />
              </div>
            </div>
          )}

          {actionKind === 'freeze' && (
            <div className="text-sm text-muted-foreground">
              Freezing prevents further credits/debits. Super-admin adjustments can still bypass.
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setWalletActionOpen(false)}>Cancel</Button>
            <Button onClick={submitAction} disabled={submitting} data-testid="wallet-action-submit">
              {submitting ? 'Working…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repay modal */}
      <Dialog open={repayOpen} onOpenChange={setRepayOpen}>
        <DialogContent data-testid="loan-repay-dialog">
          <DialogHeader>
            <DialogTitle>Repay loan — {repayLoan?.user_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Outstanding: <span className="font-bold text-foreground">{fmtINR(repayLoan?.outstanding ?? 0)}</span>
            </div>
            <div>
              <Label>Repayment amount (₹)</Label>
              <Input type="number" step="0.01" value={repayAmount} onChange={(e) => setRepayAmount(e.target.value)} data-testid="loan-repay-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRepayOpen(false)}>Cancel</Button>
            <Button onClick={submitRepay} disabled={submitting} data-testid="loan-repay-submit">
              {submitting ? 'Working…' : 'Confirm repayment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter231 — Receive Money wizard */}
      <ReceiveMoneyDialog
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        onSaved={() => { fetchAll(); fetchPendingRequests(); fetchPendingCashOuts(); }}
      />
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, tone = 'primary', testid }) {
  const toneMap = {
    primary: 'from-primary/15 via-primary/5 border-primary/30',
    amber: 'from-amber-500/15 via-amber-500/5 border-amber-500/30',
    rose: 'from-rose-500/15 via-rose-500/5 border-rose-500/30',
  };
  return (
    <Card className={`p-4 bg-gradient-to-br ${toneMap[tone]} border-2`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="font-display text-2xl font-bold tabular-nums mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}
